import { useMemo, useState, useRef, type FormEvent } from 'react';
import {
  ChevronLeft,
  ChevronRight,
  ArrowUpRight,
  Download,
  Upload,
  Smartphone,
  ShieldCheck,
  FolderOpen,
  SlidersHorizontal,
  Check,
  Info,
  Trash2,
  Globe2,
  CreditCard,
} from 'lucide-react';
import { EmptyState, ServiceIcon } from './UI';
import {
  categories,
  calendarEvents,
  addDays,
  addMonths,
  monthlyCost,
  money,
  fullDate,
  shortDate,
  daysBetween,
  type AppData,
  type Subscription,
  type Currency,
  type Settings,
} from '../lib/domain.ts';
import type { Mutate } from './Forms';

export function CostBreakdown({ data, compact = false }: { data: AppData; compact?: boolean }) {
  const totals = Object.entries(categories).map(([key, meta]) => ({
    ...meta,
    key,
    amount: data.subscriptions
      .filter((s) => s.category === key)
      .reduce((sum, s) => sum + monthlyCost(s, data.settings.rates), 0),
  }));
  const total = totals.reduce((sum, item) => sum + item.amount, 0);
  let end = 0;
  const gradient = totals
    .map((item) => {
      const start = end;
      end += total ? (item.amount / total) * 100 : 0;
      return `${item.color} ${start}% ${end}%`;
    })
    .join(', ');
  return (
    <div className={`cost-breakdown ${compact ? 'compact' : ''}`}>
      <div
        className="donut-chart"
        style={{ background: total ? `conic-gradient(${gradient})` : 'var(--border)' }}
        role="img"
        aria-label={totals.map((t) => `${t.label} ${money(t.amount)}每月`).join('，')}
      >
        <div>
          <small>月均支出</small>
          <strong>{money(total, 'CNY', 0)}</strong>
          <span>CNY / 月</span>
        </div>
      </div>
      <div className="cost-legend">
        {totals.map((item) => (
          <div key={item.key}>
            <span>
              <i style={{ background: item.color }} />
              {item.label}
            </span>
            <b>{total ? Math.round((item.amount / total) * 100) : 0}%</b>
            <strong>{money(item.amount)}</strong>
          </div>
        ))}
      </div>
    </div>
  );
}

export function CalendarPage({
  data,
  onOpen,
}: {
  data: AppData;
  onOpen: (s: Subscription) => void;
}) {
  const [month, setMonth] = useState(data.today.slice(0, 7));
  const [selected, setSelected] = useState(data.today);
  const first = `${month}-01`,
    next = addMonths(first, 1),
    last = addDays(next, -1);
  const events = useMemo(
    () => calendarEvents(data.subscriptions, first, last),
    [data.subscriptions, first, last],
  );
  const total = events
    .filter((e) => e.type === 'payment')
    .reduce((sum, e) => sum + e.amount * data.settings.rates[e.subscription.currency], 0);
  const weekday = (new Date(`${first}T00:00:00Z`).getUTCDay() + 6) % 7;
  const start = addDays(first, -weekday);
  const dayCount = Math.ceil((weekday + Number(last.slice(8))) / 7) * 7;
  const selectedEvents = events.filter((e) => e.date === selected);
  function changeMonth(delta: number) {
    const date = addMonths(first, delta);
    if (date < '1900-01-01' || date > '2200-12-31') return;
    setMonth(date.slice(0, 7));
    setSelected(date);
  }
  return (
    <>
      <div className="calendar-summary">
        <div>
          <span className="eyebrow">YOUR UPCOMING SCHEDULE</span>
          <h2>把每个日期，安排妥当。</h2>
          <p>付款和资源到期各自标记，未来周期按固定付款日推算。</p>
        </div>
        <div>
          <small>本月计划付款</small>
          <strong>{money(total)}</strong>
          <span>
            {events.filter((e) => e.type === 'payment').length} 笔付款 ·{' '}
            {events.filter((e) => e.type === 'expiry').length} 项到期
          </span>
        </div>
      </div>
      <section className="panel calendar-panel">
        <div className="panel-heading">
          <div className="calendar-month">
            <button className="icon-button" aria-label="上个月" onClick={() => changeMonth(-1)}>
              <ChevronLeft size={19} />
            </button>
            <h3>
              {Number(month.slice(0, 4))} 年 {Number(month.slice(5))} 月
            </h3>
            <button className="icon-button" aria-label="下个月" onClick={() => changeMonth(1)}>
              <ChevronRight size={19} />
            </button>
          </div>
          <div className="calendar-key">
            <span>
              <i className="payment-dot" />
              付款
            </span>
            <span>
              <i className="expiry-dot" />
              到期
            </span>
            <button
              className="button small secondary"
              onClick={() => {
                setMonth(data.today.slice(0, 7));
                setSelected(data.today);
              }}
            >
              今天
            </button>
          </div>
        </div>
        <div className="calendar-week">
          {['一', '二', '三', '四', '五', '六', '日'].map((d) => (
            <span key={d}>{d}</span>
          ))}
        </div>
        <div className="calendar-grid">
          {Array.from({ length: dayCount }, (_, i) => {
            const date = addDays(start, i);
            const dayEvents = events.filter((e) => e.date === date);
            return (
              <div
                key={date}
                className={`calendar-cell ${date.slice(0, 7) !== month ? 'outside' : ''} ${date === selected ? 'selected' : ''} ${date === data.today ? 'today' : ''}`}
                onClick={() => setSelected(date)}
              >
                <button
                  className="calendar-day"
                  aria-label={`选择 ${date}`}
                  onClick={() => setSelected(date)}
                >
                  {Number(date.slice(8))}
                </button>
                <div className="day-event-list">
                  {dayEvents.slice(0, 3).map((event) => (
                    <button
                      key={event.id}
                      className={`calendar-event ${event.type}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelected(date);
                        onOpen(event.subscription);
                      }}
                    >
                      <i />
                      <span>{event.subscription.name}</span>
                    </button>
                  ))}
                  {dayEvents.length > 3 && (
                    <span className="more-events">+{dayEvents.length - 3} 项</span>
                  )}
                </div>
                <div className="mobile-event-dots">
                  {dayEvents.slice(0, 4).map((e) => (
                    <i className={e.type} key={e.id} />
                  ))}
                </div>
              </div>
            );
          })}
        </div>
        <div className="selected-day-list">
          <div>
            <h3>{shortDate(selected)} 的安排</h3>
            <small>{selectedEvents.length} 项</small>
          </div>
          {selectedEvents.length ? (
            selectedEvents.map((event) => (
              <button
                className="calendar-agenda"
                key={event.id}
                onClick={() => onOpen(event.subscription)}
              >
                <ServiceIcon sub={event.subscription} small />
                <span>
                  <b>{event.subscription.name}</b>
                  <small>
                    {event.type === 'payment' ? '计划付款' : '资源到期'} ·{' '}
                    {event.subscription.provider}
                  </small>
                </span>
                <strong>
                  {event.type === 'payment'
                    ? money(event.amount, event.subscription.currency)
                    : '检查续费'}
                </strong>
                <ArrowUpRight size={15} />
              </button>
            ))
          ) : (
            <p className="muted">今天没有安排，轻松一点。</p>
          )}
        </div>
      </section>
      <p className="footnote">
        <Info size={13} />
        未来付款为计划推算，实际支出以记录的付款为准。日期使用北京时间。
      </p>
    </>
  );
}

export function AnalyticsPage({ data }: { data: AppData }) {
  const recurring = data.subscriptions.filter((s) => s.status === 'active');
  const monthly = recurring.reduce((sum, s) => sum + monthlyCost(s, data.settings.rates), 0);
  const budget = data.settings.monthlyBudget;
  const forecasts = Array.from({ length: 6 }, (_, i) => {
    const first = addMonths(`${data.today.slice(0, 7)}-01`, i);
    const to = addDays(addMonths(first, 1), -1);
    return {
      label: `${Number(first.slice(5, 7))} 月`,
      amount: calendarEvents(recurring, first, to)
        .filter((e) => e.type === 'payment')
        .reduce((sum, e) => sum + e.amount * data.settings.rates[e.subscription.currency], 0),
    };
  });
  const peak = Math.max(...forecasts.map((f) => f.amount), 1);
  const paidThisMonth = data.payments
    .filter((p) => p.date.startsWith(data.today.slice(0, 7)))
    .reduce((sum, p) => sum + p.amount * data.settings.rates[p.currency], 0);
  return (
    <>
      <div className="analytics-stats">
        <div className="analytics-stat">
          <small>月均订阅成本</small>
          <strong>{money(monthly)}</strong>
          <span>按付款周期折算</span>
        </div>
        <div className="analytics-stat">
          <small>年度预计成本</small>
          <strong>{money(monthly * 12)}</strong>
          <span>不含一次性付款</span>
        </div>
        <div className="analytics-stat">
          <small>本月实际已付</small>
          <strong>{money(paidThisMonth)}</strong>
          <span>来自已记录付款</span>
        </div>
      </div>
      <div className="analytics-grid">
        <section className="panel">
          <div className="panel-heading">
            <h3>支出分布</h3>
            <span className="panel-kicker">按月均成本</span>
          </div>
          <CostBreakdown data={data} />
        </section>
        <section className="panel forecast-panel">
          <div className="panel-heading">
            <h3>未来 6 个月付款计划</h3>
            <span className="panel-kicker">CNY 估算</span>
          </div>
          <div className="bar-chart">
            {forecasts.map((f, i) => (
              <div className="bar-column" key={f.label}>
                <span>{money(f.amount, 'CNY', 0)}</span>
                <div className="bar-track">
                  <div
                    className={i === 0 ? 'current' : ''}
                    style={{ height: `${Math.max(f.amount ? 5 : 0, (f.amount / peak) * 100)}%` }}
                  />
                </div>
                <small>{f.label}</small>
              </div>
            ))}
          </div>
          <p className="footnote">按当前订阅推算，不代表历史账单。</p>
        </section>
      </div>
      <section className="panel budget-panel">
        <div className="panel-heading">
          <h3>每月预算</h3>
          <span>{budget ? money(budget) : '尚未设置'}</span>
        </div>
        <div className="budget-summary">
          <strong>{budget ? `${Math.round((monthly / budget) * 100)}%` : '—'}</strong>
          <p>
            {budget
              ? monthly > budget
                ? `月均成本超出预算 ${money(monthly - budget)}`
                : `预计还余 ${money(budget - monthly)} 的预算空间`
              : '在设置中填写预算，帮助你掌握订阅开支。'}
          </p>
        </div>
        <div className={`budget-track ${monthly > budget && budget > 0 ? 'over' : ''}`}>
          <div style={{ width: `${budget ? Math.min(100, (monthly / budget) * 100) : 0}%` }} />
        </div>
      </section>
      <section className="panel">
        <div className="panel-heading">
          <h3>付款记录</h3>
          <span className="panel-kicker">{data.payments.length} 笔</span>
        </div>
        {data.payments.length ? (
          <div className="payments-table">
            <div className="payments-table-head">
              <span>订阅</span>
              <span>付款日期</span>
              <span>实际金额</span>
              <span>备注</span>
            </div>
            {data.payments.map((p) => (
              <div className="payments-table-row" key={p.id}>
                <b>{p.name}</b>
                <span>{fullDate(p.date)}</span>
                <strong>{money(p.amount, p.currency)}</strong>
                <span>{p.note || '—'}</span>
              </div>
            ))}
          </div>
        ) : (
          <EmptyState
            title="账单从第一笔付款开始"
            text="确认续费后点击「记录付款」，这里会留下真实的支出记录。"
          />
        )}
      </section>
      <p className="footnote">
        <Info size={13} />
        外币按设置中的手动汇率估算，汇率并非实时数据。
      </p>
    </>
  );
}

export function SettingsPage({
  data,
  mutate,
  onAccess,
  notify,
  confirm,
}: {
  data: AppData;
  mutate: Mutate;
  onAccess: () => void;
  notify: (s: string) => void;
  confirm: (title: string, text: string, action: () => Promise<void>) => void;
}) {
  const [settings, setSettings] = useState<Settings>(data.settings),
    [busy, setBusy] = useState(false),
    [error, setError] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const demos = data.subscriptions.filter((s) => s.isDemo).length;
  async function save(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError('');
    try {
      await mutate('/settings', 'PUT', settings, '工作空间设置已保存');
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }
  async function importFile(file?: File) {
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) {
      notify('备份文件不能超过 8 MB');
      return;
    }
    try {
      const payload = JSON.parse(await file.text());
      if (
        payload.format !== 'renewal-desk' ||
        payload.version !== 1 ||
        !Array.isArray(payload.subscriptions)
      )
        throw new Error('请使用续期导出的 JSON 备份');
      confirm(
        '合并导入备份？',
        `备份包含 ${payload.subscriptions.length} 份订阅。同一标识的订阅会更新，其他订阅会保留；导入前自动保存本机备份。`,
        async () => {
          await mutate('/import', 'POST', { data: payload, mode: 'merge' }, '备份已合并导入');
        },
      );
    } catch (e) {
      notify((e as Error).message || '备份读取失败');
    }
    if (inputRef.current) inputRef.current.value = '';
  }
  return (
    <div className="settings-grid">
      <form className="panel settings-card" onSubmit={save}>
        <div className="panel-heading">
          <h3>
            <SlidersHorizontal size={17} />
            工作空间
          </h3>
        </div>
        <label>
          名称
          <input
            required
            maxLength={40}
            value={settings.workspaceName}
            onChange={(e) => setSettings((s) => ({ ...s, workspaceName: e.target.value }))}
          />
        </label>
        <label>
          每月预算（人民币）
          <input
            type="number"
            required
            min={0}
            step="0.01"
            value={settings.monthlyBudget}
            onChange={(e) => setSettings((s) => ({ ...s, monthlyBudget: Number(e.target.value) }))}
          />
          <small>填 0 表示不设置预算</small>
        </label>
        <div className="form-section-label">
          <Globe2 size={15} />
          外币换算
        </div>
        <p className="muted settings-explanation">
          以下为手动估算汇率，可以按你的实际结算汇率修改。
        </p>
        <div className="rate-inputs">
          {(['USD', 'EUR', 'HKD'] as Currency[]).map((currency) => (
            <label key={currency}>
              1 {currency} =
              <div className="input-suffix">
                <input
                  type="number"
                  required
                  min={0.0001}
                  max={100000}
                  step="0.0001"
                  value={settings.rates[currency]}
                  onChange={(e) =>
                    setSettings((s) => ({
                      ...s,
                      rates: { ...s.rates, [currency]: Number(e.target.value) },
                    }))
                  }
                />
                <span>CNY</span>
              </div>
            </label>
          ))}
        </div>
        {error && (
          <p className="form-error" role="alert">
            {error}
          </p>
        )}
        <button className="button primary" disabled={busy} type="submit">
          <Check size={16} />
          {busy ? '保存中…' : '保存设置'}
        </button>
      </form>
      <div className="settings-column">
        <section className="panel settings-card">
          <div className="panel-heading">
            <h3>
              <Smartphone size={17} />
              {data.access.deployment === 'server' ? '跨设备访问' : '手机访问'}
            </h3>
            <span className={`status-badge ${data.access.passwordEnabled ? 'safe' : 'paused'}`}>
              {data.access.passwordEnabled ? '已开启' : '待设置'}
            </span>
          </div>
          <p className="settings-explanation">
            {data.access.deployment === 'server'
              ? '所有设备共用服务器上的同一个数据库。使用相同网址和密码，手机流量、外出网络都能继续查看和编辑。'
              : '电脑与手机共用这台电脑上的数据库。连接同一 Wi-Fi，就能在手机上继续查看和编辑。'}
          </p>
          <button className="button secondary" onClick={onAccess}>
            <Smartphone size={16} />
            {data.access.deployment === 'server' ? '访问地址与密码' : '设置手机访问'}
            <ArrowUpRight size={15} />
          </button>
        </section>
        <section className="panel settings-card">
          <div className="panel-heading">
            <h3>
              <FolderOpen size={17} />
              备份与导出
            </h3>
            <ShieldCheck size={17} className="green" />
          </div>
          <p className="settings-explanation">
            {data.access.deployment === 'server'
              ? '数据统一保存在服务器，每日自动备份。'
              : '数据保存在本机 SQLite 数据库。'}
            导入、删除和清理示例前，会自动保留最近 30 份备份。
          </p>
          <div className="export-buttons">
            <a className="button secondary" href="/api/export/json" download>
              <Download size={15} />
              JSON 备份
            </a>
            <a className="button secondary" href="/api/export/csv" download>
              <Download size={15} />
              CSV 表格
            </a>
            <a className="button secondary" href="/api/export/ics" download>
              <CreditCard size={15} />
              日历提醒
            </a>
            <button className="button secondary" onClick={() => inputRef.current?.click()}>
              <Upload size={15} />
              导入备份
            </button>
          </div>
          <input
            className="sr-only"
            ref={inputRef}
            type="file"
            accept=".json,application/json"
            aria-label="导入 JSON 备份"
            onChange={(e) => void importFile(e.target.files?.[0])}
          />
          <small className="settings-note">
            日历包含未来 12 个月的计划，可导入 Apple 日历、Google 日历等。提醒由日历应用发送。
          </small>
        </section>
        <section className="panel settings-card">
          <div className="panel-heading">
            <h3>示例数据</h3>
            <span className="panel-kicker">{demos} 份</span>
          </div>
          <p className="settings-explanation">
            示例仅用于体验界面，清除后即可开始记录你自己的订阅。
          </p>
          <button
            className="button secondary danger-text"
            disabled={!demos}
            onClick={() =>
              confirm(
                '清除全部示例数据？',
                `将移除 ${demos} 份示例及其付款记录，你自己添加的订阅会保留。`,
                async () => {
                  await mutate('/clear-demo', 'POST', {}, '示例数据已清除');
                },
              )
            }
          >
            <Trash2 size={15} />
            清除示例数据
          </button>
        </section>
        <div className="about-card">
          <span className="brand-mini">
            续期 <b>RENEW</b>
          </span>
          <p>把每一份订阅，放在心上。</p>
          <small>
            v1.0 · {data.access.deployment === 'server' ? '在线工作空间' : '本地运行'} · 独立实现
          </small>
          <div>
            <a href="https://github.com/ellite/Wallos" target="_blank" rel="noreferrer">
              参考 Wallos <ArrowUpRight size={11} />
            </a>
            <a href="https://github.com/hxx0611/Domain-Monitor" target="_blank" rel="noreferrer">
              Domain Monitor <ArrowUpRight size={11} />
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
